import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { useAuthFetch } from './AuthComponent';

/**
 * Manager Override barcode printout. Sized to a standard credit card
 * (85.6mm × 53.98mm — the ISO/IEC 7810 ID-1 spec) so it can be printed
 * onto card stock, laminated, or slipped into a lanyard.
 *
 * Screen preview shows the same card on a plain background so the admin
 * can see the layout before printing. `@page` rules trim the paper to
 * card dimensions with zero margin — most printers will center it on
 * whatever stock is loaded. Auto-fires the print dialog once the barcode
 * renders.
 */
export default function ManagerBarcodePrint() {
    const { userId } = useParams();
    const storeFetch = useAuthFetch();
    const [user, setUser] = useState(null);
    const [err, setErr]   = useState(null);
    const svgRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await storeFetch(`/api/portal-users/${userId}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const u = await res.json();
                if (!u.is_manager || !u.manager_barcode) {
                    setErr('This portal user is not a manager (no override barcode).');
                    return;
                }
                setUser(u);
            } catch (e) {
                setErr(e.message || 'Failed to load user');
            }
        })();
    }, [userId, storeFetch]);

    useEffect(() => {
        if (!user || !svgRef.current) return;
        try {
            // Bar width is in px (SVG units). Height too. Scaled by CSS
            // to fit the card exactly. Values tuned so an 18-char CODE128
            // renders at roughly 70mm wide × 12mm tall inside the card.
            JsBarcode(svgRef.current, user.manager_barcode, {
                format: 'CODE128',
                width: 1.8,
                height: 55,
                displayValue: true,
                fontSize: 12,
                margin: 0,
                textMargin: 2,
                background: '#ffffff',
                lineColor: '#000000',
            });
            setTimeout(() => { try { window.print(); } catch {} }, 400);
        } catch (e) {
            setErr(`Barcode render failed: ${e.message || e}`);
        }
    }, [user]);

    if (err) {
        return (
            <div style={{ padding: 40, fontFamily: 'system-ui, sans-serif' }}>
                <h2>Manager Barcode</h2>
                <p style={{ color: '#c81e1e' }}>{err}</p>
            </div>
        );
    }
    if (!user) {
        return <div style={{ padding: 40, fontFamily: 'system-ui, sans-serif' }}>Loading…</div>;
    }

    return (
        <>
            <style>{`
                @page { size: 85.6mm 53.98mm; margin: 0; }
                html, body { margin: 0; padding: 0; background: #f3f4f6; }
                @media print {
                    html, body { background: #ffffff; }
                    .screen-only { display: none !important; }
                    .card { box-shadow: none !important; margin: 0 !important; }
                }
                .card {
                    width: 85.6mm;
                    height: 53.98mm;
                    box-sizing: border-box;
                    padding: 4mm 5mm;
                    background: #ffffff;
                    border: 0.4mm solid #111827;
                    border-radius: 3mm;
                    box-shadow: 0 4px 14px rgba(0,0,0,0.12);
                    margin: 20mm auto;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    font-family: system-ui, -apple-system, "SF Pro Display", "Segoe UI", sans-serif;
                    color: #111827;
                }
                .card__top {
                    display: flex; justify-content: space-between; align-items: flex-start;
                    gap: 3mm;
                }
                .card__eyebrow {
                    font-size: 6pt; letter-spacing: 0.06em;
                    text-transform: uppercase; color: #6b7280; font-weight: 700;
                    line-height: 1.1;
                }
                .card__name {
                    font-size: 13pt; font-weight: 700; letter-spacing: -0.005em;
                    line-height: 1.15;
                }
                .card__tag {
                    font-size: 6pt; font-weight: 700;
                    color: #ffffff; background: #111827;
                    padding: 0.6mm 1.6mm; border-radius: 1mm;
                    align-self: flex-start;
                    letter-spacing: 0.08em; text-transform: uppercase;
                    white-space: nowrap;
                }
                .card__barcode {
                    display: flex; justify-content: center; align-items: center;
                    flex: 1; padding: 1mm 0;
                }
                .card__barcode svg {
                    max-width: 100%;
                    height: auto;
                }
                .card__footer {
                    font-size: 5.5pt; color: #6b7280; text-align: center;
                    line-height: 1.25;
                }
                .print-btn {
                    display: block; margin: 12mm auto 24mm;
                    padding: 10px 20px; font-size: 14px; font-weight: 600;
                    background: #111827; color: #fff; border: none;
                    border-radius: 8px; cursor: pointer;
                }
            `}</style>
            <div className="card">
                <div className="card__top">
                    <div>
                        <div className="card__eyebrow">Operations Hub</div>
                        <div className="card__name">{user.first_name} {user.last_name}</div>
                    </div>
                    <div className="card__tag">Manager Override</div>
                </div>
                <div className="card__barcode">
                    <svg ref={svgRef} />
                </div>
                <div className="card__footer">
                    Scan in Picking / Pressing / Packing portal, then enter your PIN.
                </div>
            </div>
            <button className="print-btn screen-only" onClick={() => window.print()}>Print</button>
        </>
    );
}
